import api from "@core/plugins/api";
import { AuthDTO } from "@core/dto/common/auth.dto";
import { CORE_EVENTS, API_URL_COMET } from "@core/constants";
import { EventManager } from "@core/managers/event.manager";
import { Logger } from "@core/helpers/logger.helpers";
import { UserStorageManager } from "./user-storage.manager";

// BO
const authBOUrl = () => `login`;
// Etab
const authEtabUrl = () => `loginEtab`;
const logoutEtabUrl = () => `/api/et/logout`;
const sendMagicLinkEtabUrl = (email) => `/generate/magic-link/${email}`;
const authWithMagicEtabUrl = () => `magic-link/login`;
// Candidate
const authCandidateUrl = () => `/auth/ex/login`;
const sendMagicLinkCandidateUrl = () => `/auth/magic-link/request`;
const authWithMagicCandidateUrl = (email, expiration, hash) => `/auth/magic-link/check?user=${email}&expires=${expiration}&hash=${hash}`;

/**
 * Core Authentication Service with its own HTTP instance
 * @class AuthenticationManager
 */
export class AuthenticationManager {
  /**
   * Return true if a token exists
   * We assume the existence of a token means there is an authenticated user
   * @returns {Promise<Boolean>} whether the user is authenticated or not
   */
  static async isUserAuthenticated() {
    return (await UserStorageManager.getToken()) !== null;
  }

  // #region BO

  /**
   * Log into BO external API
   * @param {Object} authData
   * @returns {Boolean} login success
   */
  static async loginBO({ login, password }) {
    if (login && password) {
      try {
        const dto = new AuthDTO({ login, password });
        const config = {
          handleError: false, // we do not handle the errors in order to bypass the 401
          headers: { Authorization: `Basic ${dto.getBase64Credentials()}` },
        };
        const response = await api.public.get(authBOUrl(), config);
        const user = response.data;

        await AuthenticationManager.postLoginBO({ user });

        const loginSuccess = user && user.token !== undefined;

        return loginSuccess;
      } catch (error) {
        throw error;
      }
    } else {
      return Promise.reject(new Error("AuthenticationManager.login: login and password needed"));
    }
  }

  /**
   * Post login BO actions, store token
   * @static
   * @param {Object} user
   * @property {String} token
   */
  static async postLoginBO({ user = {} }) {
    try {
      const { token } = user;
      if (token) {
        await UserStorageManager.setToken(token);
      }
    } catch (error) {
      throw new Error("AuthenticationManager.postLoginBO failed", error);
    }
  }

  /**
   * Log out and delete localstorage
   * @returns {Promise}
   */
  static async logoutBO() {
    // clear the localstorage
    return UserStorageManager.removeToken();
  }

  // #endregion BO

  // #region Etab

  /**
   * Log into Etab external API
   * @param {Object} authData
   * @returns {Boolean} login success
   */
  static async login({ login, password }) {
    if (login && password) {
      try {
        const dto = new AuthDTO({ login, password });
        const config = {
          handleError: false, // we do not handle the errors in order to bypass the 401
          headers: { Authorization: `Basic ${dto.getBase64Credentials()}` },
        };

        const response = await api.public.get(authEtabUrl(), config);
        const user = response.data;

        await AuthenticationManager.postLoginEtab({ user });

        const loginSuccess = user && user.token !== undefined;

        return loginSuccess;
      } catch (error) {
        throw error;
      }
    } else {
      return Promise.reject(new Error("AuthenticationManager.login: login and password needed"));
    }
  }

  /**
   * Log into external API using a temporary token
   * @param {Object} tempToken
   * @returns {Boolean} login success
   */
  static async loginWithTemporaryToken({ temporaryToken }) {
    if (temporaryToken) {
      try {
        const data = { temporary_token: temporaryToken };
        const response = await api.public.post(authWithMagicEtabUrl(), data, { handleError: false });
        const user = response.data;
        await AuthenticationManager.postLoginEtab({ user });

        const loginSuccess = user && user.token !== undefined;

        return loginSuccess;
      } catch (error) {
        throw error;
      }
    } else {
      throw new Error("AuthenticationManager.loginWithTemporaryToken: temporaryToken needed");
    }
  }

  /**
   * Send the generate magic link request to the API
   * @static
   * @param {String} email - the user email
   * @returns {HTTPResponse}
   */
  static async generateMagicLink({ email }) {
    return api.public.post(sendMagicLinkEtabUrl(email));
  }

  /**
   * Post login Etab actions, store user data
   * @static
   * @param {Object} user
   * @property {String} token
   */
  static async postLoginEtab({ user = {} }) {
    try {
      const { token } = user;
      if (token) {
        await UserStorageManager.setUser(user);
        await UserStorageManager.setToken(token);
        EventManager.emit(CORE_EVENTS.ON_LOGIN);
      }
    } catch (error) {
      throw new Error("AuthenticationManager.postLoginEtab failed", error);
    }
  }

  /**
   * Log out ETAB
   * @returns {Promise}
   */
  static async logout() {
    try {
      // contact the server to tell we want to logout
      // we don't want the error to be handled
      await api.private.post(logoutEtabUrl(), {}, { handleError: false });
    } catch (error) {
      // fail silently
      Logger.warn("AuthenticationManager.logout() failure", error);
    } finally {
      // whatever happens, we clean everything
      // once everything is handled, we can safely clear the localstorage
      await UserStorageManager.removeToken();
      await UserStorageManager.removeUser();

      EventManager.emit(CORE_EVENTS.ON_LOGOUT);
    }
  }

  // #endregion Etab

  // #region Candidates

  /**
   * Log into Candidate external API
   * @param {Object} authData
   * @returns {Boolean} login success
   */
  static async loginCandidate({ login, password }) {
    if (login && password) {
      try {
        const dto = new AuthDTO({ login, password });
        const data = dto.postLoginCandidateData();
        const config = {
          handleError: false, // we do not handle the errors in order to bypass the 401
        };

        // loginCandidate is on the not intercepted default api
        const response = await api.public.post(API_URL_COMET + authCandidateUrl(), data, config);
        const token = response.data?.token;
        const refreshToken = response.data?.refresh_token;
        await AuthenticationManager.postLoginCandidate({ token, refreshToken });

        const loginSuccess = token !== undefined;

        return loginSuccess;
      } catch (error) {
        throw error;
      }
    } else {
      return Promise.reject(new Error("AuthenticationManager.loginCandidate: login and password needed"));
    }
  }

  /**
   * Log into external API using a temporary token
   * @returns {Boolean} login success
   */
  static async magicLoginCandidate({ user, expires, hash }) {
    if (user && expires && hash) {
      try {
        const encodedEmail = encodeURIComponent(user);
        const response = await api.comet.post(authWithMagicCandidateUrl(encodedEmail, expires, hash), { handleError: false });
        const token = response.data?.token;
        await AuthenticationManager.postLoginCandidate({ token });

        const loginSuccess = token !== undefined;

        return loginSuccess;
      } catch (error) {
        throw error;
      }
    } else {
      throw new Error("AuthenticationManager.loginWithTemporaryToken: temporaryToken needed");
    }
  }

  /**
   * Send the magic link request to the API
   * @static
   * @param {String} email - the user email
   * @returns {HTTPResponse}
   */
  static async sendMagicLinkCandidate(email) {
    return api.comet.post(sendMagicLinkCandidateUrl(), { email });
  }

  /**
   * Post login Candidate actions, store user data
   * @static
   * @param {Object} token
   */
  static async postLoginCandidate({ token, refreshToken }) {
    try {
      if (token) {
        await UserStorageManager.setToken(token);
        if (refreshToken) {
          await UserStorageManager.setRefreshToken(refreshToken);
        }
        EventManager.emit(CORE_EVENTS.ON_LOGIN);
      }
    } catch (error) {
      throw new Error("AuthenticationManager.postLoginCandidate failed", error);
    }
  }

  /**
   * Log out CANDIDATE and delete localstorage
   * @returns {Promise}
   */
  static async logoutCandidate() {
    EventManager.emit(CORE_EVENTS.ON_LOGOUT);
    // once everything is handled, we can safely clear the localstorage
    return UserStorageManager.removeToken();
  }

  // #endregion Candidates
}
